/**
 * Auth state — single source of truth for the current user.
 *
 * On mount: if a token is present, fetches `/api/auth/me`. ONLY a 401
 * discards the token and routes to `/login`; any other failure (a
 * 502/503 mid-deploy, a tenant-resolver 404, a network drop) keeps the
 * token and `<ProtectedRoute>` shows why the check failed with a retry.
 * The `auth:expired` event from the API client triggers the 401 path
 * mid-session.
 *
 * Exposes `useAuth()` and a `<ProtectedRoute>` wrapper to guard pages.
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Spinner } from "@pervasivemind/bi-components";

import {
  getToken,
  HttpError,
  onAuthExpired,
  setToken,
} from "@/lib/api";
import { useT } from "@/i18n";
import { authApi } from "@/lib/builder-api";
import type { User } from "@/types";

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/** What a failed `/api/auth/me` means for the stored session. Only a
 *  401 says the token is bad — and request() already announced it via
 *  auth:expired. A 502/503 mid-deploy, a 404 from the tenant resolver
 *  or a network failure says nothing about the token: keep it and say
 *  why the check failed (owner decision, audit #267). */
export function meFailureOutcome(err: unknown): {
  dropToken: boolean;
  error: string | null;
} {
  if (err instanceof HttpError && err.status === 401) {
    return { dropToken: true, error: null };
  }
  return {
    dropToken: false,
    error: err instanceof Error ? err.message : String(err),
  };
}

export function AuthProvider({ children }: AuthProviderProps): ReactNode {
  const [state, setState] = useState<AuthState>(() => ({
    user: null,
    loading: getToken() !== null,
    error: null,
  }));
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      if (mounted.current) {
        setState({ user: null, loading: false, error: null });
      }
      return;
    }
    if (mounted.current) setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const user = await authApi.me();
      if (mounted.current) {
        setState({ user, loading: false, error: null });
      }
    } catch (err) {
      const outcome = meFailureOutcome(err);
      if (outcome.dropToken) setToken(null);
      if (mounted.current) {
        setState({ user: null, loading: false, error: outcome.error });
      }
    }
  }, []);

  // Initial hydrate.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Cross-component logout when any request returns 401.
  useEffect(() => {
    return onAuthExpired(() => {
      setToken(null);
      if (mounted.current) {
        setState({ user: null, loading: false, error: null });
      }
    });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      // `tenant` is the customer the address turned out to belong to
      // (Plan E, E4). The login page is fleet-wide and carries no
      // prefix, so this response is the ONLY place the browser learns
      // which `/{slug}/` to enter and which key to store the session
      // under.
      const { access_token, tenant } = await authApi.login(email, password);
      if (!tenant) {
        // Fail loud rather than store a session nothing can find again.
        throw new Error(
          "The server did not say which instance this account belongs to.",
        );
      }
      setToken(access_token, tenant);
      // A full navigation, not a client-side one: every API path derives
      // from `window.location`, so the app has to be RUNNING inside the
      // customer prefix before it asks anything else — starting with
      // `/api/auth/me`.
      window.location.assign(`/${tenant}/`);
      return;
    } catch (err) {
      setToken(null);
      const message =
        err instanceof HttpError ? err.message : "Login failed";
      if (mounted.current) {
        setState({ user: null, loading: false, error: message });
      }
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setState({ user: null, loading: false, error: null });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, refresh }),
    [state, login, logout, refresh],
  );

  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

interface ProtectedRouteProps {
  children: ReactNode;
}

/** Renders children only when authenticated. Redirects to /login otherwise. */
export function ProtectedRoute({ children }: ProtectedRouteProps): ReactNode {
  const { user, loading, error, refresh } = useAuth();
  const { t } = useT();
  const location = useLocation();
  const loadingLabel = t("common.loading");

  if (loading) {
    // This is the FIRST screen of every cold load, and it used to be a
    // hardcoded English "Loading…" in grey — no spinner, no brand, and
    // the only screen in the app that never spoke Spanish. It now uses
    // the same Spinner every other loading state uses, and the same
    // dictionary. (The file is .ts, not .tsx, hence createElement.)
    return createElement(
      "div",
      {
        className:
          "flex min-h-screen items-center justify-center gap-2 text-sm text-neutral-600",
      },
      createElement(Spinner, { size: 18, ariaLabel: loadingLabel }),
      loadingLabel,
    );
  }

  if (!user && error !== null) {
    // The token is still stored — this is not a sign-out. Show why the
    // session check failed and let the person retry.
    return createElement(
      "div",
      {
        role: "alert",
        className:
          "flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-sm text-neutral-700",
      },
      createElement(
        "p",
        { className: "font-medium text-error-dark" },
        t("auth.sessionCheckFailed"),
      ),
      createElement(
        "pre",
        {
          className:
            "max-h-40 max-w-xl overflow-auto whitespace-pre-wrap text-xs text-neutral-600",
        },
        error,
      ),
      createElement(
        "button",
        {
          type: "button",
          onClick: () => void refresh(),
          className:
            "rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50",
        },
        t("common.retry"),
      ),
    );
  }

  if (!user) {
    return createElement(Navigate, {
      to: "/login",
      state: { from: location.pathname },
      replace: true,
    });
  }

  return children;
}
